import type React from 'react';
import { useIsDesktop } from './useIsDesktop';

export const COMPOSER_PLACEHOLDER = 'Write message...';
export const COMPOSER_PLACEHOLDER_WITH_HINT = 'Type a message… (use Shift + Enter for a new line)';

type UseSendOnEnterOptions = {
  onSend: () => void;
  /** Gates the send itself (e.g. empty input or disabled composer), not the newline suppression. */
  canSend: boolean;
  /** Compact composers always show the short placeholder. */
  isCompact?: boolean;
};

/**
 * Single source of truth for composer Enter behavior across the satellite apps: at the desktop
 * layout breakpoint Enter sends (Shift+Enter inserts a newline) and the placeholder carries the
 * hint; below it Enter inserts a newline and the placeholder is short.
 */
export const useSendOnEnter = ({ onSend, canSend, isCompact = false }: UseSendOnEnterOptions) => {
  const sendsOnEnter = useIsDesktop();

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key !== 'Enter' || e.shiftKey || !sendsOnEnter) {
      return;
    }
    e.preventDefault();
    if (canSend) {
      onSend();
    }
  };

  const placeholder = sendsOnEnter && !isCompact ? COMPOSER_PLACEHOLDER_WITH_HINT : COMPOSER_PLACEHOLDER;

  return { onKeyDown, placeholder };
};
